// The owner pipeline board: tracks, phases and stages, as labels and groupings.
//
// No imports on purpose, so web/test/ownerBoard.test.ts can load it under
// node --test --experimental-strip-types.
//
// What this file does NOT decide: which track a person is in, which of his
// applications represents him, or whether he is live. Those are decided in
// Postgres (v_owner_track_position, read by owner_board_v2), where the vehicle
// register is the source record. This file only arranges the rows the database
// returns into groups a person can scan.

export type TrackCode = 'asset_finance' | 'brought_in' | 'brings_own_vehicle'

export type Track = {
  code: TrackCode
  label: string
  short: string
  blurb: string
  // Display order only. Since 0287 no track hides another: a person in Fleetly
  // more than one way shows on each track they are in.
  rank: number
  // The stages this track moves through, in order. Shown even at zero so a
  // track with nobody in it yet still shows its shape.
  stages: string[]
}

// Highest first, which is also the order the tabs read left to right.
export const TRACKS: Track[] = [
  {
    code: 'asset_finance',
    label: 'Cash for car',
    short: 'Cash for car',
    blurb: 'The owner puts cash in and Fleetly buys the car.',
    rank: 3,
    stages: [
      'submitted', 'not_contacted', 'responded', 'meeting_booked',
      'documents_pending', 'contracting', 'contract_pending', 'onboarding',
      'onboarded', 'offboarding',
    ],
  },
  {
    code: 'brought_in',
    label: 'Car to new model',
    short: 'New model',
    blurb: 'The owner brings his own car into the 12 month agreement at R7,700 a month.',
    rank: 2,
    stages: [
      'submitted', 'not_contacted', 'responded', 'meeting_booked',
      'vehicle_assessment', 'finance_consent_pending', 'documents_pending',
      'uber_approval_pending', 'contracting', 'contract_pending', 'onboarding',
      'onboarded', 'offboarding',
    ],
  },
  {
    code: 'brings_own_vehicle',
    label: 'Old model',
    short: 'Old model',
    blurb: 'The historic arrangement. Live cars here are the candidates for the new model.',
    rank: 1,
    stages: [
      'submitted', 'not_contacted', 'responded', 'meeting_booked',
      'sourcing_vehicle', 'documents_pending', 'uber_approval_pending',
      'contracting', 'contract_pending', 'onboarding', 'onboarded', 'offboarding',
    ],
  },
]

export function trackOf(code: string | null | undefined): Track | undefined {
  return TRACKS.find((t) => t.code === code)
}

export function trackLabel(code: string | null | undefined): string {
  return trackOf(code)?.label ?? 'No track'
}

// Every stage with a plain-language label, in workflow order.
export const STAGE_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  not_contacted: 'Not contacted',
  responded: 'Responded',
  meeting_booked: 'Meeting booked',
  sourcing_vehicle: 'Sourcing vehicle',
  vehicle_assessment: 'Vehicle assessment',
  documents_pending: 'Documents pending',
  finance_consent_pending: 'Finance house consent',
  uber_approval_pending: 'Uber approval pending',
  contracting: 'Contracting',
  contract_pending: 'Contract sent',
  onboarding: 'Onboarding',
  onboarded: 'Live',
  offboarding: 'Offboarding',
  waitlist: 'Waitlist',
  not_qualified: 'Not qualified',
  meeting_unsuccessful: 'Meeting unsuccessful',
  uber_rejected: 'Uber rejected',
  offboarded: 'Offboarded',
}

export function stageLabel(code: string | null | undefined): string {
  if (!code) return 'Unknown'
  return STAGE_LABELS[code] ?? code.split('_').join(' ')
}

// Stages that are not a live piece of work: parked or finished.
export const PARKED_STAGES = ['waitlist']
export const CLOSED_STAGES = ['not_qualified', 'meeting_unsuccessful', 'uber_rejected', 'offboarded']
export const LIVE_STAGES = ['onboarded', 'offboarding']

// The phases a stage belongs to. Phases are the headings the list is grouped
// under; a stage is a group inside its phase.
export type PhaseCode = 'leads' | 'meeting' | 'qualifying' | 'contract' | 'onboarding' | 'live' | 'parked' | 'closed'

export const PHASES: { code: PhaseCode; label: string; stages: string[]; openByDefault: boolean }[] = [
  { code: 'leads', label: 'New leads', stages: ['submitted', 'not_contacted', 'responded'], openByDefault: true },
  { code: 'meeting', label: 'Meeting', stages: ['meeting_booked'], openByDefault: true },
  {
    code: 'qualifying',
    label: 'Qualifying the car',
    stages: ['sourcing_vehicle', 'vehicle_assessment', 'documents_pending', 'finance_consent_pending', 'uber_approval_pending'],
    openByDefault: true,
  },
  { code: 'contract', label: 'Contract', stages: ['contracting', 'contract_pending'], openByDefault: true },
  { code: 'onboarding', label: 'Onboarding', stages: ['onboarding'], openByDefault: true },
  { code: 'live', label: 'Live', stages: LIVE_STAGES, openByDefault: true },
  { code: 'parked', label: 'Parked', stages: PARKED_STAGES, openByDefault: false },
  { code: 'closed', label: 'Closed', stages: CLOSED_STAGES, openByDefault: false },
]

export function phaseOf(stage: string | null | undefined): PhaseCode {
  for (const p of PHASES) if (stage && p.stages.includes(stage)) return p.code
  return 'leads'
}

// Stage order across every track, used to sort groups.
export const STAGE_ORDER: string[] = PHASES.flatMap((p) => p.stages)

export function stageTone(stage: string | null | undefined): 'brand' | 'amber' | 'green' | 'red' | 'neutral' {
  const phase = phaseOf(stage)
  if (phase === 'live') return 'green'
  if (phase === 'closed') return 'red'
  if (phase === 'parked') return 'amber'
  if (phase === 'leads') return 'neutral'
  return 'brand'
}

// A row is stale when it is live work (not parked, closed or live) and has sat
// in its stage longer than this.
export const STALE_DAYS = 30

export type BoardRowLike = {
  owner_model: string | null
  current_status: string
  days_in_stage: number | null
}

export function isOpenWork(stage: string): boolean {
  return !PARKED_STAGES.includes(stage) && !CLOSED_STAGES.includes(stage) && !LIVE_STAGES.includes(stage)
}

export function isStale(row: BoardRowLike): boolean {
  return isOpenWork(row.current_status) && (row.days_in_stage ?? 0) > STALE_DAYS
}

export type SegmentCode = 'all' | 'open' | 'stale' | 'live' | 'parked' | 'closed'

export const SEGMENTS: { code: SegmentCode; label: string; help: string }[] = [
  { code: 'all', label: 'Everyone', help: 'Every person in this view.' },
  { code: 'open', label: 'In progress', help: 'Leads through onboarding: work someone should be moving.' },
  { code: 'stale', label: `Stuck over ${STALE_DAYS} days`, help: `In progress and in the same stage for more than ${STALE_DAYS} days.` },
  { code: 'live', label: 'Live', help: 'A car in the fleet, per the vehicle register.' },
  { code: 'parked', label: 'Waitlist', help: 'Parked on the waitlist.' },
  { code: 'closed', label: 'Closed', help: 'Not qualified, unsuccessful, rejected or offboarded.' },
]

export function inSegment(row: BoardRowLike, seg: SegmentCode): boolean {
  switch (seg) {
    case 'all': return true
    case 'open': return isOpenWork(row.current_status)
    case 'stale': return isStale(row)
    case 'live': return LIVE_STAGES.includes(row.current_status)
    case 'parked': return PARKED_STAGES.includes(row.current_status)
    case 'closed': return CLOSED_STAGES.includes(row.current_status)
  }
}

export type StageGroup<T> = { stage: string; phase: PhaseCode; rows: T[] }

// Group rows by stage, in stage order, oldest in stage first within a group.
export function groupByStage<T extends BoardRowLike>(rows: T[]): StageGroup<T>[] {
  const map = new Map<string, T[]>()
  for (const r of rows) {
    const list = map.get(r.current_status)
    if (list) list.push(r)
    else map.set(r.current_status, [r])
  }
  const order = (s: string) => {
    const i = STAGE_ORDER.indexOf(s)
    return i === -1 ? STAGE_ORDER.length : i
  }
  return [...map.entries()]
    .sort((a, b) => order(a[0]) - order(b[0]))
    .map(([stage, list]) => ({
      stage,
      phase: phaseOf(stage),
      rows: [...list].sort((a, b) => (b.days_in_stage ?? 0) - (a.days_in_stage ?? 0)),
    }))
}

export type TrackSummary = { total: number; open: number; stale: number; live: number }

export function summarise<T extends BoardRowLike>(rows: T[]): TrackSummary {
  let open = 0, stale = 0, live = 0
  for (const r of rows) {
    if (isOpenWork(r.current_status)) open += 1
    if (isStale(r)) stale += 1
    if (LIVE_STAGES.includes(r.current_status)) live += 1
  }
  return { total: rows.length, open, stale, live }
}

export function daysLabel(days: number | null | undefined): string {
  if (days == null) return '—'
  if (days === 0) return 'today'
  if (days === 1) return '1 day'
  if (days < 60) return `${days} days`
  const months = Math.round(days / 30)
  return `${months} months`
}
